import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataGrid, GridFooterContainer } from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import AlertManager from "../../shared/Alert/Alert";
import {
  exportLandedCostDataViewCsv,
  generateLandedCostDataView,
} from "../../API/landedCostDataViewApi";
import { downloadTemplateFile } from "../../API/landedCostVendorDownloadTemplateFileApi";
import layoutClasses from "../LandedCostUpload/LandedCostUpload.module.css";
import tableClasses from "../../shared/LandedCostUploadHistoryTable/LandedCostUploadHistory.module.css";

const formatAsInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(end.getMonth() - 1);
  return {
    startDate: formatAsInputDate(start),
    endDate: formatAsInputDate(end),
  };
};

const formatDateParts = (value) => {
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    if (year && month && day) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  if (value instanceof Date) {
    return formatAsInputDate(value);
  }
  if (typeof value === "string" && value.trim().length) {
    return value;
  }
  return "--";
};

const parseDateValue = (value) => {
  if (value instanceof Date) return value;
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    if (year && month && day) {
      const parsedYear = Number(year);
      const parsedMonth = Number(month);
      const parsedDay = Number(day);
      if (!Number.isNaN(parsedYear) && !Number.isNaN(parsedMonth) && !Number.isNaN(parsedDay)) {
        return new Date(parsedYear, parsedMonth - 1, parsedDay);
      }
    }
  }
  if (typeof value === "string" && value.trim().length) {
    const timestamp = Date.parse(value);
    if (!Number.isNaN(timestamp)) {
      return new Date(timestamp);
    }
  }
  return null;
};

const normalizeFilterDate = (value) => {
  if (!value) return null;
  const dateValue = value instanceof Date ? value : new Date(value);
  return Number.isNaN(dateValue.getTime()) ? null : dateValue;
};

const toDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  return value;
};

const DEFAULT_ROWS_PER_PAGE = 100;
const ROWS_PER_PAGE_OPTIONS = [100, 250, 500];

const LandedCostDataView = () => {
  const fileInputRef = useRef(null);
  const requestIdRef = useRef(0);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [startDate, setStartDate] = useState(() => getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(() => getDefaultDateRange().endDate);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [gridResetKey, setGridResetKey] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);

  const CustomDateFilterInput = (props) => {
    const { item, applyValue } = props;
    const [selectedDate, setSelectedDate] = useState(
      item.value ? normalizeFilterDate(item.value) : null
    );

    const handleChange = (newDate) => {
      setSelectedDate(newDate);
      applyValue({ ...item, value: newDate ? format(newDate, "yyyy-MM-dd") : null });
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Select Date"
          value={selectedDate}
          onChange={handleChange}
          views={["year", "month", "day"]}
          renderInput={(params) => <TextField {...params} fullWidth />}
        />
      </LocalizationProvider>
    );
  };

  const CustomDateRangeFilterInput = (props) => {
    const { item, applyValue } = props;
    const [from, setFrom] = useState(item.value?.from ? normalizeFilterDate(item.value.from) : null);
    const [to, setTo] = useState(item.value?.to ? normalizeFilterDate(item.value.to) : null);

    const handleChange = (nextFrom, nextTo) => {
      if (!nextFrom && !nextTo) {
        applyValue({ ...item, value: null });
        return;
      }
      applyValue({
        ...item,
        value: {
          from: nextFrom ? format(nextFrom, "yyyy-MM-dd") : null,
          to: nextTo ? format(nextTo, "yyyy-MM-dd") : null,
        },
      });
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <DatePicker
            label="From Date"
            value={from}
            onChange={(newDate) => {
              setFrom(newDate);
              handleChange(newDate, to);
            }}
            views={["year", "month", "day"]}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
          <DatePicker
            label="To Date"
            value={to}
            onChange={(newDate) => {
              setTo(newDate);
              handleChange(from, newDate);
            }}
            views={["year", "month", "day"]}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </div>
      </LocalizationProvider>
    );
  };

  const dateFilterOperators = useMemo(
    () => [
      {
        label: "is",
        value: "is",
        getApplyFilterFn: (filterItem) => {
          const targetDate = normalizeFilterDate(filterItem.value);
          if (!filterItem.columnField || !targetDate) return null;
          return ({ value }) => {
            const cellDate = normalizeFilterDate(value);
            if (!cellDate) return false;
            return (
              cellDate.getFullYear() === targetDate.getFullYear() &&
              cellDate.getMonth() === targetDate.getMonth() &&
              cellDate.getDate() === targetDate.getDate()
            );
          };
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "is after",
        value: "is after",
        getApplyFilterFn: (filterItem) => {
          const targetDate = normalizeFilterDate(filterItem.value);
          if (!filterItem.columnField || !targetDate) return null;
          return ({ value }) => {
            const cellDate = normalizeFilterDate(value);
            return cellDate ? cellDate > targetDate : false;
          };
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "is before",
        value: "is before",
        getApplyFilterFn: (filterItem) => {
          const targetDate = normalizeFilterDate(filterItem.value);
          if (!filterItem.columnField || !targetDate) return null;
          return ({ value }) => {
            const cellDate = normalizeFilterDate(value);
            return cellDate ? cellDate < targetDate : false;
          };
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "date range",
        value: "date range",
        getApplyFilterFn: (filterItem) => {
          const { from, to } = filterItem.value || {};
          const fromDate = normalizeFilterDate(from);
          const toDate = normalizeFilterDate(to);
          if (!fromDate && !toDate) return null;
          return ({ value }) => {
            const cellDate = normalizeFilterDate(value);
            if (!cellDate) return false;
            if (fromDate && toDate) return cellDate >= fromDate && cellDate <= toDate;
            if (fromDate) return cellDate >= fromDate;
            if (toDate) return cellDate <= toDate;
            return true;
          };
        },
        InputComponent: CustomDateRangeFilterInput,
      },
    ],
    []
  );

  const columns = useMemo(
    () => [
      { field: "parsedSku", headerName: "Parsed SKU #", flex: 1, minWidth: 140 },
      { field: "hqLine", headerName: "HQ Line", flex: 0.7, minWidth: 100 },
      { field: "fieldLine", headerName: "Field Line", flex: 0.7, minWidth: 110 },
      { field: "asgVendorAccountNumber", headerName: "ASG Vendor #", flex: 1, minWidth: 140 },
      { field: "asgVendorName", headerName: "ASG Vendor Name", flex: 1.2, minWidth: 180 },
      { field: "asgVendorGroupCode", headerName: "ASG Vendor Group", flex: 1, minWidth: 140 },
      { field: "asgVendorProductNumber", headerName: "ASG Vendor Product #", flex: 1.1, minWidth: 170 },
      { field: "asgPrimaryVendorAccountNumber", headerName: "ASG Primary Vendor #", flex: 1.1, minWidth: 170 },
      { field: "productLifeCycleStateName", headerName: "Product Lifecycle", flex: 1, minWidth: 150 },
      { field: "countryOfOrigin", headerName: "COO", flex: 0.6, minWidth: 90 },
      {
        field: "unitCostDate",
        headerName: "Unit Cost Date",
        flex: 0.9,
        minWidth: 130,
        type: "date",
        valueGetter: ({ row }) => parseDateValue(row.unitCostDateRaw ?? row.unitCostDate),
        valueFormatter: ({ value }) => formatDateParts(value),
        filterOperators: dateFilterOperators,
      },
      // { field: "productWeight", headerName: "Weight", flex: 0.8, minWidth: 110 },
      // { field: "productHeight", headerName: "Height", flex: 0.8, minWidth: 110 },
      // { field: "productWidth", headerName: "Width", flex: 0.8, minWidth: 110 },
      // { field: "productDepth", headerName: "Depth", flex: 0.8, minWidth: 110 },
      { field: "palletQuantity", headerName: "Pallet Qty", flex: 0.8, minWidth: 110 },
      { field: "masterCartonQuantity", headerName: "Master Carton Qty", flex: 1, minWidth: 150 },
      { field: "fobCostUnit", headerName: "FOB Unit", flex: 0.9, minWidth: 120 },
      { field: "fobCost", headerName: "FOB Cost", flex: 0.9, minWidth: 120 },
      { field: "oldLandedCost", headerName: "Old Landed Cost", flex: 1, minWidth: 140 },
      { field: "tariffSec301", headerName: "Tariff Sec 301", flex: 0.9, minWidth: 130 },
      { field: "tariffSec232", headerName: "Tariff Sec 232", flex: 0.9, minWidth: 130 },
      { field: "tariffSec122", headerName: "Tariff Sec 122", flex: 0.9, minWidth: 130 },
      { field: "tariffAmt", headerName: "Tariff Amount", flex: 1, minWidth: 140 },
      { field: "skuExcluded", headerName: "SKU Excluded", flex: 0.9, minWidth: 120 },
      { field: "sec232Percent", headerName: "% Component for Sec 232", flex: 1.2, minWidth: 180 },
      { field: "rate40", headerName: '40" Rate', flex: 0.9, minWidth: 120 },
      { field: "rate20", headerName: '20" Rate', flex: 0.9, minWidth: 120 },
      { field: "containerType", headerName: "Container Type", flex: 1, minWidth: 140 },
      { field: "antiDumpingRate", headerName: "Anti-Dumping Rate", flex: 1, minWidth: 150 },
      { field: "counterVailingRate", headerName: "Countervailing Rate", flex: 1, minWidth: 160 },
      { field: "poStatusValue", headerName: "PO Status", flex: 0.9, minWidth: 120 },
      { field: "poProductNumber", headerName: "PO Product #", flex: 1, minWidth: 130 },
      { field: "poDate", headerName: "PO Date", flex: 0.9, minWidth: 120 },
      { field: "poVendorAccountNumber", headerName: "PO Vendor #", flex: 1, minWidth: 130 },
      { field: "approvedDate", headerName: "Approved Date", flex: 0.9, minWidth: 130 },
      { field: "approvedBy", headerName: "Approved By", flex: 0.9, minWidth: 130 },
      { field: "calculatedLandedCost", headerName: "Calculated Landed Cost", flex: 1.2, minWidth: 180 },
      { field: "dcCost", headerName: "DC Cost", flex: 0.9, minWidth: 120 },
      { field: "marginStrategy", headerName: "Margin Strategy", flex: 1, minWidth: 140 },
      {
        field: "createDate",
        headerName: "Last updated date",
        flex: 0.9,
        minWidth: 130,
        type: "date",
        valueGetter: ({ row }) => parseDateValue(row.createDateRaw ?? row.createDate),
        valueFormatter: ({ value }) => formatDateParts(value),
        filterOperators: dateFilterOperators,
      },
    ],
    [dateFilterOperators]
  );

  const normalizedRows = useMemo(() => {
    if (Array.isArray(tableData) && tableData.length) {
      return tableData.map((row, index) => ({
        id:
          row.id ??
          row.uuid ??
          row.parsedSku ??
          row.poProductNumber ??
          row.poVendorAccountNumber ??
          `row-${index}`,
        parsedSku: toDisplay(row.parsedSku ?? row.sku),
        hqLine: toDisplay(row.hqLine),
        fieldLine: toDisplay(row.fieldLine),
        asgVendorAccountNumber: toDisplay(row.asgVendorAccountNumber),
        asgVendorName: toDisplay(row.asgVendorName),
        asgVendorGroupCode: toDisplay(row.asgVendorGroupCode),
        asgVendorProductNumber: toDisplay(row.asgVendorProductNumber),
        asgPrimaryVendorAccountNumber: toDisplay(row.asgPrimaryVendorAccountNumber),
        productLifeCycleStateName: toDisplay(row.productLifeCycleStateName),
        countryOfOrigin: toDisplay(row.countryOfOrigin),
        unitCostDateRaw: row.unitCostDate ?? row.unit_cost_date,
        unitCostDate: formatDateParts(row.unitCostDate ?? row.unit_cost_date),
        // productWeight: toDisplay(row.productWeight),
        // productHeight: toDisplay(row.productHeight),
        // productWidth: toDisplay(row.productWidth),
        // productDepth: toDisplay(row.productDepth),
        palletQuantity: toDisplay(row.palletQuantity),
        masterCartonQuantity: toDisplay(row.masterCartonQuantity),
        fobCostUnit: toDisplay(row.fobCostUnit),
        fobCost: toDisplay(row.fobCost),
        oldLandedCost: toDisplay(row.oldLandedCost),
        tariffSec301: toDisplay(row.tariffSec301),
        tariffSec232: toDisplay(row.tariffSec232),
        tariffSec122: toDisplay(row.tariffSec122),
        tariffAmt: toDisplay(row.tariffAmt),
        skuExcluded: toDisplay(row.skuExcluded),
        sec232Percent: toDisplay(row.sec232Percent),
        rate40: toDisplay(row.rate40),
        rate20: toDisplay(row.rate20),
        containerType: toDisplay(row.containerType),
        antiDumpingRate: toDisplay(row.antiDumpingRate),
        counterVailingRate: toDisplay(row.counterVailingRate),
        poStatusValue: toDisplay(row.poStatusValue),
        poProductNumber: toDisplay(row.poProductNumber),
        poDate: formatDateParts(row.poDate ?? row.po_date),
        poVendorAccountNumber: toDisplay(row.poVendorAccountNumber),
        isNew: toDisplay(row.isNew),
        isApproved: toDisplay(row.isApproved),
        isLCCalculated: toDisplay(row.isLCCalculated),
        areParamsMissing: toDisplay(row.areParamsMissing),
        approvedDate: formatDateParts(row.approvedDate),
        approvedBy: toDisplay(row.approvedBy),
        createDateRaw: row.createDate ?? row.create_date,
        createDate: formatDateParts(row.createDate ?? row.create_date),
        calculatedLandedCost: toDisplay(row.calculatedLandedCost),
        dcCost: toDisplay(row.dcCost),
        marginStrategy: toDisplay(row.marginStrategy),
      }));
    }
    return [
      {
        id: "placeholder",
        parsedSku: "No data to display. Adjust the date range and refresh.",
        isPlaceholder: true,
      },
    ];
  }, [tableData]);

  const handleFileChange = (event) => {
    const file = event?.target?.files?.[0];
    setSelectedFileName(file ? file.name : "");
  };

  const validateInputs = ({ requireFile = false, requireBothDates = true } = {}) => {
    const file = fileInputRef.current?.files?.[0];
    if (requireFile && !file) {
      AlertManager.error("Select a file before continuing.", { contactSupport: false });
      return false;
    }

    const hasStart = Boolean(startDate);
    const hasEnd = Boolean(endDate);

    if (requireBothDates) {
      if (!hasStart || !hasEnd) {
        AlertManager.error("Provide both start and end dates.", { contactSupport: false });
        return false;
      }
    } else if (!hasStart && !hasEnd) {
      AlertManager.error("Provide at least a start or end date.", { contactSupport: false });
      return false;
    }

    if (hasStart && hasEnd && new Date(startDate) > new Date(endDate)) {
      AlertManager.error("Start date must be earlier than end date.", { contactSupport: false });
      return false;
    }

    return true;
  };

  const clearFileSelection = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setSelectedFileName("");
  };

  const resetTable = () => {
    setTableData([]);
    setRowCount(0);
    setTotalPages(0);
    setPage(0);
    setGridResetKey((value) => value + 1);
  };

  const loadDataView = async ({ requireFile = false, pageIndex = 0, nextRowsPerPage = rowsPerPage } = {}) => {
    if (!validateInputs({ requireFile, requireBothDates: true })) {
      setTableData([]);
      setRowCount(0);
      setTotalPages(0);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const file = fileInputRef.current?.files?.[0];
    const payload = {
      file,
      startDate,
      endDate,
      page: pageIndex,
      size: nextRowsPerPage,
    };

    setLoading(true);
    setTableData([]);
    try {
      const response = await generateLandedCostDataView(payload);
      if (requestId !== requestIdRef.current) return;
      if (Array.isArray(response)) {
        setTableData(response);
        setRowCount(response.length);
        setTotalPages(response.length ? 1 : 0);
        setPage(pageIndex);
      } else if (Array.isArray(response?.content) || Array.isArray(response?.rows)) {
        const rows = response.content ?? response.rows;
        setTableData(rows);
        setRowCount(Number.isFinite(response.totalElements) ? response.totalElements : rows.length);
        setTotalPages(Number.isFinite(response.totalPages) ? response.totalPages : rows.length ? 1 : 0);
        setPage(Number.isFinite(response.pageNumber) ? response.pageNumber : pageIndex);
      } else {
        resetTable();
        clearFileSelection();
      }
    } catch (error) {
      console.error("Failed to generate landed cost data view", error);
      if (requestId === requestIdRef.current) {
        resetTable();
        clearFileSelection();
        AlertManager.error("Unable to generate data view.", { contactSupport: false });
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadDataView({ requireFile: false, pageIndex: 0, nextRowsPerPage: rowsPerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTemplateDownload = async () => {
    try {
      await downloadTemplateFile("DATAVIEW");
    } catch (error) {
      console.error("Failed to download the data view template", error);
      AlertManager.error("Unable to download the data view template.", { contactSupport: false });
    }
  };

  const isGenerateEnabled = !loading;
  const isExportDisabled = loading || exportLoading;
  const hasSelectedFile = Boolean(selectedFileName || fileInputRef.current?.value);

  const CustomPagination = () => {
    const pageCount = totalPages || Math.max(1, Math.ceil((rowCount || 0) / rowsPerPage));
    const rowsPerPageValue = String(rowsPerPage);

    const handleRowsPerPageChange = (event) => {
      const nextRowsPerPage = Number(event.target.value);
      if (!Number.isFinite(nextRowsPerPage)) {
        return;
      }
      setRowsPerPage(nextRowsPerPage);
      setPage(0);
      loadDataView({ requireFile: false, pageIndex: 0, nextRowsPerPage });
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
            Rows per page
          </Typography>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={rowsPerPageValue}
              onChange={handleRowsPerPageChange}
              displayEmpty
              renderValue={(value) => value}
              inputProps={{ "aria-label": "Rows per page" }}
              data-testid="rows-per-page-select"
            >
              {ROWS_PER_PAGE_OPTIONS.map((option) => (
                <MenuItem key={option} value={String(option)}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Pagination
          color="primary"
          count={pageCount}
          page={page + 1}
          onChange={(_, value) => {
            const nextPage = value - 1;
            setPage(nextPage);
            loadDataView({ requireFile: false, pageIndex: nextPage, nextRowsPerPage: rowsPerPage });
          }}
        />
      </Box>
    );
  };

  const CustomFooter = () => (
    <GridFooterContainer>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          py: 1.5,
          pr: 2,
        }}
      >
        <CustomPagination />
      </Box>
    </GridFooterContainer>
  );

  const handleExport = async () => {
    if (!validateInputs({ requireFile: false, requireBothDates: true })) {
      return;
    }

    try {
      setExportLoading(true);
      await exportLandedCostDataViewCsv({
        file: fileInputRef.current?.files?.[0],
        startDate,
        endDate,
      });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <section className={layoutClasses.body}>
      <header className={layoutClasses.heading}>Landed Cost Data View</header>

      <Box className={layoutClasses.container}>
        <Box className={layoutClasses.fieldRow}>
          <Button component="label" variant="outlined" size="small">
            Choose File
            <input
              hidden
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              data-testid="data-view-file-input"
            />
          </Button>
          <Typography variant="body2" className={layoutClasses.fileNameDisplay}>
            {selectedFileName || "No file selected"}
          </Typography>
          {hasSelectedFile && (
            <IconButton
              size="small"
              aria-label="Remove selected file"
              onClick={clearFileSelection}
              data-testid="data-view-file-remove"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box className={layoutClasses.fieldRow}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Box>
        <Box className={layoutClasses.actions}>
          <Button
            variant="contained"
            onClick={() => loadDataView({ requireFile: false, pageIndex: 0 })}
            disabled={!isGenerateEnabled}
            className={layoutClasses.uploadBtn}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
          <Tooltip title="Download the landed cost data view template">
            <span>
              <Button
                className={layoutClasses.uploadBtn}
                variant="text"
                onClick={handleTemplateDownload}
                disabled={loading}
                data-testid="data-view-template-download"
              >
                Download Template
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Typography variant="body2" className={layoutClasses.helperText}>
          Data defaults to the last 1 month. Adjust the dates or upload a spreadsheet if needed, then
          refresh to view results.
        </Typography>
      </Box>

      <div className={layoutClasses.hLine} />

      <div className={layoutClasses.fileuploadHistory}>
        <div className={tableClasses.tableActions}>
          <Button
            variant="contained"
            size="small"
            onClick={handleExport}
            disabled={isExportDisabled}
            className={tableClasses.exportButton}
            startIcon={
              exportLoading ? (
                <CircularProgress color="inherit" size={14} thickness={5} />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            data-testid="data-view-export"
          >
            {exportLoading ? "Exporting..." : "Export"}
          </Button>
        </div>
        <div className={tableClasses.wrapper}>
          <DataGrid
            key={gridResetKey}
            rows={normalizedRows}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            components={{ Footer: CustomFooter }}
            getRowId={(row) => row.id}
            getRowClassName={(params) => (params.row.isPlaceholder ? "placeholder-row" : "")}
            rowHeight={28}
            columnHeaderHeight={32}
            sx={{
              "& .MuiDataGrid-cell": {
                fontSize: "0.78rem",
                py: 0.15,
              },
              "& .MuiDataGrid-columnHeaders": {
                fontSize: "0.82rem",
              },
              "& .MuiDataGrid-columnHeadersInner": {
                minHeight: 32,
              },
              "& .MuiDataGrid-row": {
                minHeight: 28,
              },
              "& .placeholder-row .MuiDataGrid-cell": {
                fontStyle: "italic",
                color: "#7a7a7a",
              },
            }}
            loading={loading}
          />
        </div>
      </div>
    </section>
  );
};

export default LandedCostDataView;
